import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Zap, Plus, Trash2, Play, Loader2,
  TrendingUp, TrendingDown, Minus, ArrowRight,
  AlertTriangle, Clock, Package, Wrench
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCENARIO_TYPES = [
  { value: "rush_order", label: "Rush Order", icon: Zap, description: "Add an urgent order" },
  { value: "machine_down", label: "Machine Down", icon: Wrench, description: "Simulate equipment failure" },
  { value: "quantity_change", label: "Quantity Change", icon: Package, description: "Modify order quantity" },
  { value: "material_delay", label: "Material Delay", icon: Clock, description: "Delay material availability" },
];

const DEFAULT_PARAMS = {
  rush_order: { material: "FG2_CP", quantity: 100, due_date: "" },
  machine_down: { work_center: "WC-ASSM-01", days: 3 },
  quantity_change: { material: "", multiplier: 1.5 },
  material_delay: { material: "", delay_days: 7 },
};

const WhatIfPanel = ({ onResult, materials = [] }) => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const addScenario = (type) => {
    const defaultDue = new Date(Date.now() + 3 * 86400000).toISOString().split('T')[0];
    const params = { ...DEFAULT_PARAMS[type] };
    if (type === "rush_order") params.due_date = defaultDue;
    setScenarios([...scenarios, { type, params }]);
  };

  const removeScenario = (idx) => {
    setScenarios(scenarios.filter((_, i) => i !== idx));
    if (scenarios.length <= 1) setResult(null);
  };

  const updateParam = (idx, key, value) => {
    const updated = [...scenarios];
    updated[idx].params[key] = value;
    setScenarios(updated);
  };

  const runAnalysis = async () => {
    if (scenarios.length === 0) {
      toast.error("Add at least one scenario");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/schedule/what-if`, {
        scenarios,
        optimize_for: "on_time",
      }, { timeout: 120000 });
      setResult(response.data);
      onResult?.(response.data);
      toast.success("What-if analysis complete");
    } catch (error) {
      toast.error(`Analysis failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const DeltaIndicator = ({ value, label, inverse = false }) => {
    const isPositive = inverse ? value < 0 : value > 0;
    const isNegative = inverse ? value > 0 : value < 0;
    return (
      <div className="text-center">
        <div className={`text-lg font-bold ${isPositive ? 'text-emerald-600' : isNegative ? 'text-red-600' : 'text-muted-foreground'}`}>
          {value > 0 ? '+' : ''}{value}
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="what-if-panel">
      {/* Scenario Builder */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />
            Define Scenarios
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Add scenario buttons */}
          <div className="flex flex-wrap gap-2">
            {SCENARIO_TYPES.map((st) => (
              <Button
                key={st.value}
                variant="outline"
                size="sm"
                onClick={() => addScenario(st.value)}
                data-testid={`add-scenario-${st.value}`}
                className="text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                {st.label}
              </Button>
            ))}
          </div>

          {/* Active scenarios */}
          {scenarios.map((scenario, idx) => {
            const typeInfo = SCENARIO_TYPES.find(st => st.value === scenario.type);
            const Icon = typeInfo?.icon || Zap;
            return (
              <div key={idx} className="border rounded-lg p-3 space-y-2 bg-secondary/30" data-testid={`scenario-${idx}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">{typeInfo?.label}</span>
                    <Badge variant="secondary" className="text-xs">{typeInfo?.description}</Badge>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeScenario(idx)} data-testid={`remove-scenario-${idx}`}>
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {scenario.type === "rush_order" && (
                    <>
                      <div>
                        <label className="text-xs text-muted-foreground">Material</label>
                        <Input
                          value={scenario.params.material}
                          onChange={(e) => updateParam(idx, "material", e.target.value)}
                          className="h-8 text-xs"
                          placeholder="Material ID"
                          data-testid={`scenario-${idx}-material`}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Quantity</label>
                        <Input
                          type="number"
                          value={scenario.params.quantity}
                          onChange={(e) => updateParam(idx, "quantity", parseFloat(e.target.value) || 0)}
                          className="h-8 text-xs"
                          data-testid={`scenario-${idx}-quantity`}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Due Date</label>
                        <Input
                          type="date"
                          value={scenario.params.due_date}
                          onChange={(e) => updateParam(idx, "due_date", e.target.value)}
                          className="h-8 text-xs"
                          data-testid={`scenario-${idx}-due-date`}
                        />
                      </div>
                    </>
                  )}

                  {scenario.type === "machine_down" && (
                    <>
                      <div>
                        <label className="text-xs text-muted-foreground">Work Center</label>
                        <Input
                          value={scenario.params.work_center}
                          onChange={(e) => updateParam(idx, "work_center", e.target.value)}
                          className="h-8 text-xs"
                          data-testid={`scenario-${idx}-work-center`}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Days Down</label>
                        <Input
                          type="number"
                          value={scenario.params.days}
                          onChange={(e) => updateParam(idx, "days", parseInt(e.target.value) || 1)}
                          className="h-8 text-xs"
                          data-testid={`scenario-${idx}-days`}
                        />
                      </div>
                    </>
                  )}

                  {scenario.type === "quantity_change" && (
                    <>
                      <div>
                        <label className="text-xs text-muted-foreground">Material</label>
                        <Input
                          value={scenario.params.material}
                          onChange={(e) => updateParam(idx, "material", e.target.value)}
                          className="h-8 text-xs"
                          placeholder="Material ID"
                          data-testid={`scenario-${idx}-material`}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Multiplier</label>
                        <Input
                          type="number"
                          step="0.1"
                          value={scenario.params.multiplier}
                          onChange={(e) => updateParam(idx, "multiplier", parseFloat(e.target.value) || 1)}
                          className="h-8 text-xs"
                          data-testid={`scenario-${idx}-multiplier`}
                        />
                      </div>
                    </>
                  )}

                  {scenario.type === "material_delay" && (
                    <>
                      <div>
                        <label className="text-xs text-muted-foreground">Material</label>
                        <Input
                          value={scenario.params.material}
                          onChange={(e) => updateParam(idx, "material", e.target.value)}
                          className="h-8 text-xs"
                          placeholder="Material ID"
                          data-testid={`scenario-${idx}-material`}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Delay (days)</label>
                        <Input
                          type="number"
                          value={scenario.params.delay_days}
                          onChange={(e) => updateParam(idx, "delay_days", parseInt(e.target.value) || 1)}
                          className="h-8 text-xs"
                          data-testid={`scenario-${idx}-delay-days`}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {scenarios.length > 0 && (
            <Button onClick={runAnalysis} disabled={loading} className="w-full" data-testid="run-what-if-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Run What-If Analysis
            </Button>
          )}

          {scenarios.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">
              Click a button above to add a scenario, then run the analysis.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Results Comparison */}
      {result && (
        <Card data-testid="what-if-results">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              Impact Analysis
            </CardTitle>
            <div className="flex flex-wrap gap-1 mt-1">
              {result.scenarios?.map((desc, i) => (
                <Badge key={i} variant="outline" className="text-xs">{desc}</Badge>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {/* KPI Comparison */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <Card className="p-3 bg-secondary/20">
                <div className="text-center">
                  <div className="text-xs text-muted-foreground mb-1">Base</div>
                  <div className="text-xl font-bold">{result.base_schedule?.on_time_orders}/{result.base_schedule?.total_orders}</div>
                  <div className="text-xs text-muted-foreground">On-Time ({result.impact?.base_otd_rate}%)</div>
                </div>
              </Card>

              <div className="flex flex-col items-center justify-center">
                <ArrowRight className="w-5 h-5 text-muted-foreground" />
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <DeltaIndicator value={result.impact?.on_time_change} label="On-Time" />
                  <DeltaIndicator value={result.impact?.late_change} label="Late" inverse />
                </div>
              </div>

              <Card className="p-3 bg-primary/5 border-primary/20">
                <div className="text-center">
                  <div className="text-xs text-muted-foreground mb-1">Modified</div>
                  <div className="text-xl font-bold">{result.modified_schedule?.on_time_orders}/{result.modified_schedule?.total_orders}</div>
                  <div className="text-xs text-muted-foreground">On-Time ({result.impact?.modified_otd_rate}%)</div>
                </div>
              </Card>
            </div>

            {/* Hours comparison */}
            <div className="grid grid-cols-3 gap-4 text-center text-sm">
              <div>
                <span className="text-muted-foreground text-xs">Base Hours</span>
                <p className="font-semibold">{result.base_schedule?.total_production_hours}h</p>
              </div>
              <div>
                <span className="text-muted-foreground text-xs">Change</span>
                <p className={`font-semibold ${result.impact?.hours_change > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {result.impact?.hours_change > 0 ? '+' : ''}{result.impact?.hours_change}h
                </p>
              </div>
              <div>
                <span className="text-muted-foreground text-xs">Modified Hours</span>
                <p className="font-semibold">{result.modified_schedule?.total_production_hours}h</p>
              </div>
            </div>

            {result.impact?.late_change > 0 && (
              <div className="mt-3 p-2 rounded bg-red-50 border border-red-200 flex items-center gap-2 text-xs text-red-700">
                <AlertTriangle className="w-4 h-4" />
                This scenario causes {result.impact.late_change} additional late order(s).
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WhatIfPanel;
